import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { CheckCircleIcon, LockClosedIcon, DocumentTextIcon } from '@heroicons/react/solid';
import { HFP_V2_SCHEMAS } from '../../schemas/hfpV2';
import { completionPercent } from '../../utils/referralStatus';
import { isConditionalMet } from '../../utils/schemaEvaluator';
import { apiHfpTestGetOverview } from '../../utils/hfpTestApi';
import { loadHfpTestSession, clearHfpTestSession, HFP_TEST_TILES } from './hfpTestSession';
import HfpTestGenerateProfileModal from './HfpTestGenerateProfileModal';

// Per-question time estimate MUST match RmsFormTemplate's in-form footer
// (perQuestionSeconds = 30) so the overview and the form show identical minutes.
const SECONDS_PER_QUESTION = 30;

// Resolve a stored answer to its selected option id (radio stores {optionId} or
// a bare value). Mirrors RmsFormTemplate's `v?.optionId ?? v`.
function selectedOptionId(answers, questionId) {
  const v = answers?.[questionId];
  if (v && typeof v === 'object') return v.optionId ?? null;
  return v ?? null;
}

// Is a question visible for this role and (if conditional) its gate answer?
// Mirrors the respondent + conditionalOn visibility used in the form.
function isQuestionVisible(q, roleSuffix, answers) {
  const vis = q.meta?.hfp?.respondent || 'both';
  const roleOk = vis === 'both'
    || (vis === 'lg_only' && roleSuffix === 'guardian')
    || (vis === 'school_only' && roleSuffix === 'school_user');
  if (!roleOk) return false;
  // Use the SAME conditional gate the form uses (raw compare of the stored
  // answer) so the overview's question count/minutes match the in-form footer.
  return isConditionalMet(answers, q);
}

// Count total visible + answered questions for a form, from the stored answers.
// This matches the in-form calc (visibleSteps vs isStepAnswered) so minutes agree.
function formCounts(formKey, roleSuffix, answers) {
  const schema = HFP_V2_SCHEMAS[formKey];
  if (!schema) return { total: 0, answered: 0, remaining: 0 };
  let total = 0;
  let answered = 0;
  (schema.sections || []).forEach((sec) => {
    (sec.questions || []).forEach((q) => {
      if (q.type === 'info') return;
      if (!isQuestionVisible(q, roleSuffix, answers)) return;
      total += 1;
      const sel = selectedOptionId(answers, q.questionId);
      if (sel !== null && sel !== undefined && sel !== '') answered += 1;
    });
  });
  return { total, answered, remaining: Math.max(total - answered, 0) };
}

// Minutes-remaining text — byte-for-byte the same rules as RmsFormTemplate.
function timeRemainingText(remaining) {
  if (remaining === 0) return 'All questions answered';
  const minutes = Math.round((remaining * SECONDS_PER_QUESTION) / 60);
  if (minutes < 1) return 'Less than 1 minute remaining';
  if (minutes === 1) return 'About 1 minute remaining';
  return `About ${minutes} minutes remaining`;
}

/**
 * HfpTestDashboard
 * ----------------
 * Staging-only mirror of ServiceUserPatientOverview after selecting a CYP, but
 * showing only the 7 HFP forms. Progress comes from the rmsHfpTestGetOverview
 * callable (the unauthenticated site cannot read Firestore directly). Linear
 * workflow: the first incomplete form is unlocked; later ones stay locked;
 * completed ones reopen in review mode.
 */
export default function HfpTestDashboard() {
  const navigate = useNavigate();
  const session = loadHfpTestSession();

  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Post-completion step: the "generate the profile" dialog (fills in the
  // other respondent's answers, then builds the HFP).
  const [generateModalOpen, setGenerateModalOpen] = useState(false);

  const timelineRef = useRef(null);
  const firstDotRef = useRef(null);
  const lastDotRef = useRef(null);
  const [lineDims, setLineDims] = useState({ top: 0, height: 0, left: null });

  const patientRecordId = session?.patientRecordId;
  const roleSuffix = session?.roleSuffix;

  const fetchOverview = useCallback(async () => {
    if (!patientRecordId || !roleSuffix) return;
    try {
      const res = await apiHfpTestGetOverview({ patientRecordId, roleSuffix });
      setOverview(res);
      setError(null);
    } catch (err) {
      setError(err.message || 'Could not load your forms.');
    } finally {
      setLoading(false);
    }
  }, [patientRecordId, roleSuffix]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  // Refetch when the tab regains focus (e.g. returning from a form) so progress
  // stays current.
  useEffect(() => {
    const onFocus = () => fetchOverview();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchOverview]);

  const tiles = useMemo(() => {
    const forms = overview?.forms || {};
    return HFP_TEST_TILES.map((t) => {
      const f = forms[t.formKey] || {};
      // completionPercent enforces the RMS invariant: 100 (complete) ONLY when
      // the form is submitted; drafts are capped at 99. This prevents a form
      // from showing "complete" just because the backend percent hit 100 (HFP
      // questions are optional, so raw percent inflates on the first save).
      const percent = completionPercent(f.status, f.percent);
      const isComplete = percent >= 100;
      const { remaining } = formCounts(t.formKey, roleSuffix, f.answers || {});
      return { ...t, percent, isComplete, remaining };
    });
  }, [overview, roleSuffix]);

  const currentIndex = useMemo(() => tiles.findIndex((t) => !t.isComplete), [tiles]);
  const allComplete = currentIndex === -1 && tiles.length > 0;

  // Measure the connecting rail between the first and last status dots.
  useEffect(() => {
    const compute = () => {
      try {
        const container = timelineRef.current;
        const firstEl = firstDotRef.current;
        const lastEl = lastDotRef.current;
        if (!container || !firstEl || !lastEl) return;
        const cRect = container.getBoundingClientRect();
        const fRect = firstEl.getBoundingClientRect();
        const lRect = lastEl.getBoundingClientRect();
        const fCenterY = fRect.top + fRect.height / 2;
        const lCenterY = lRect.top + lRect.height / 2;
        const fCenterX = fRect.left + fRect.width / 2;
        const top = Math.max(0, fCenterY - cRect.top);
        const height = Math.max(0, lCenterY - fCenterY);
        const left = fCenterX - cRect.left - 2;
        setLineDims({ top, height, left });
      } catch { /* noop */ }
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [tiles]);

  const handleStartOver = useCallback(() => {
    clearHfpTestSession();
    navigate('/rms/hfp-test');
  }, [navigate]);

  if (!session) {
    return <Navigate to="/rms/hfp-test" replace />;
  }

  const childName = overview?.child?.firstName || session.childFirstName || 'the child';
  const roleLabel = roleSuffix === 'guardian' ? 'Parent / Carer' : 'School Representative';
  const counterpart = overview?.counterpart || {};
  const hfpAvailable = counterpart.generated && (overview?.hfp || {}).scoringReady;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-secondary/15 flex flex-col">
      <div className="w-full md:w-2/3 lg:w-1/2 mx-auto px-6 md:px-10 py-10">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{childName}&apos;s Holistic Functional Profile</h1>
            <p className="text-sm text-gray-600 mt-1">
              Filling in as <span className="font-medium">{roleLabel}</span>.
            </p>
          </div>
          <button
            type="button"
            onClick={handleStartOver}
            className="flex-shrink-0 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Start over
          </button>
        </div>

        {loading ? (
          <div className="text-gray-600 py-12 text-center">Loading your forms…</div>
        ) : error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-3">{error}</div>
        ) : (
          <>
            {allComplete && (
              <div className="mb-6 rounded-xl border-2 border-green-500 bg-green-50 px-4 py-3 text-sm text-green-800">
                All 7 forms are complete. Thank you — you can revisit any form to review your answers.
              </div>
            )}

            {/* Once the tester's 7 forms are submitted, surface the HFP the
               same way the real service-user overview does (icon card + View
               button). On the test site the second respondent doesn't exist,
               so "Generate" opens a dialog that fills in the other answers
               and builds the profile. */}
            {allComplete && (
              <div className="mb-6 flex flex-col sm:flex-row sm:items-start gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex items-start gap-4 flex-grow min-w-0">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <DocumentTextIcon className="w-5 h-5 text-primary" aria-hidden="true" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-base font-semibold text-gray-900">Holistic Functional Profile</div>
                    <p className="text-sm text-gray-600">
                      An overview of {childName}&apos;s strengths and support needs at home and at school.
                    </p>
                    {hfpAvailable && (
                      <button
                        type="button"
                        onClick={() => setGenerateModalOpen(true)}
                        className="mt-1 text-xs text-gray-500 underline hover:text-gray-700"
                      >
                        Generate again with a different agreement level
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex flex-shrink-0 items-center gap-2 sm:pl-0 pl-14">
                  {hfpAvailable ? (
                    <button
                      type="button"
                      onClick={() => navigate('/rms/hfp-test/report')}
                      className="px-3 py-2 rounded-lg border border-primary bg-primary text-white text-sm font-semibold shadow-sm hover:bg-primary/90 transition-colors"
                    >
                      View
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setGenerateModalOpen(true)}
                      className="px-3 py-2 rounded-lg border border-primary bg-primary text-white text-sm font-semibold shadow-sm hover:bg-primary/90 transition-colors"
                    >
                      Generate
                    </button>
                  )}
                </div>
              </div>
            )}

            <div ref={timelineRef} className="relative w-full">
              {lineDims.height > 0 && lineDims.left !== null && (
                <div
                  className="absolute bg-primary rounded-full"
                  style={{ top: `${lineDims.top}px`, height: `${lineDims.height}px`, left: `${lineDims.left}px`, width: '4px' }}
                  aria-hidden="true"
                />
              )}
              <div className="mb-1 pt-2 pl-12 text-xl font-bold text-gray-900">Strength and Support Needs</div>
              <ol className="space-y-2">
                {tiles.map((tile, idx) => {
                  const { percent, isComplete } = tile;
                  const isCurrent = !isComplete && currentIndex === idx;
                  const isLocked = !isComplete && !isCurrent && currentIndex >= 0 && idx > currentIndex;
                  const isClickable = isCurrent || isComplete;
                  const subtitle = isComplete ? 'All questions completed' : timeRemainingText(tile.remaining);

                  const compactBtn = 'w-full text-left px-3 py-3 rounded-xl border transition duration-150 focus:outline-none focus:ring-2 shadow-sm';
                  const stateStyles = isComplete
                    ? 'bg-green-50 border-2 border-green-500'
                    : isCurrent
                      ? 'bg-amber-50 border-2 border-amber-400'
                      : 'bg-gray-100 border-2 border-gray-300 opacity-80 cursor-not-allowed';
                  const hoverStyles = isLocked
                    ? ''
                    : (isComplete
                        ? 'hover:bg-green-100 hover:border-green-500'
                        : (isCurrent ? 'hover:bg-amber-100 hover:border-primary-400' : 'hover:bg-gray-50 hover:border-gray-300'));
                  const labelStyles = isLocked ? 'text-gray-400' : 'text-gray-900';
                  const subLabelStyles = isLocked ? 'text-gray-400' : 'text-gray-600';

                  return (
                    <li key={tile.key} className="relative pl-12">
                      <div
                        className="absolute left-[20px] -translate-x-1/2 top-1/2 -translate-y-1/2 z-10"
                        ref={idx === 0 ? firstDotRef : (idx === tiles.length - 1 ? lastDotRef : undefined)}
                        aria-hidden="true"
                      >
                        {isComplete ? (
                          <span className="relative inline-flex items-center justify-center w-8 h-8 rounded-full">
                            <span className="absolute inset-0 m-auto w-5 h-5 rounded-full bg-white" />
                            <CheckCircleIcon className="relative w-8 h-8 text-green-500" />
                          </span>
                        ) : isLocked ? (
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border-2 border-gray-400 text-gray-400 bg-white">
                            <LockClosedIcon className="w-4 h-4" />
                          </span>
                        ) : percent > 0 ? (
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border-2 border-amber-400 text-amber-500 bg-white">
                            <svg viewBox="0 0 20 20" className="w-3.5 h-3.5"><circle cx="10" cy="10" r="8" fill="currentColor" /></svg>
                          </span>
                        ) : (
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border-2 border-gray-400 text-gray-400 bg-white">
                            <svg viewBox="0 0 20 20" className="w-3.5 h-3.5"><circle cx="10" cy="10" r="8" fill="currentColor" className="opacity-30" /></svg>
                          </span>
                        )}
                      </div>

                      <button
                        type="button"
                        className={`${compactBtn} ${stateStyles} ${hoverStyles} pl-4 pr-4`}
                        style={{ '--tw-ring-color': '#e7c22f', '--tw-ring-opacity': '0.5' }}
                        aria-disabled={!isClickable}
                        onClick={() => {
                          if (!isClickable) return;
                          navigate(isComplete ? `/rms/hfp-test/forms/${tile.key}?mode=review` : `/rms/hfp-test/forms/${tile.key}`);
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className={`text-base font-semibold ${labelStyles}`}>{tile.title}</div>
                            <div className={`text-xs ${subLabelStyles}`}>{subtitle}</div>
                          </div>
                          {!isLocked && (
                            <svg viewBox="0 0 20 20" className="w-4 h-4 text-gray-500">
                              <path d="M7 5l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ol>
            </div>
          </>
        )}
      </div>

      <HfpTestGenerateProfileModal
        isOpen={generateModalOpen}
        onClose={() => setGenerateModalOpen(false)}
        onView={() => navigate('/rms/hfp-test/report')}
        onGenerated={fetchOverview}
        childName={childName}
        roleSuffix={roleSuffix}
        patientRecordId={patientRecordId}
      />
    </div>
  );
}
